import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Trophy } from 'lucide-react';
import ranksService, { BiblicalRank, getRankLore, getRankImagePath, getTierColor } from '../services/ranksService';
import authService from '../services/authService';
import userService from '../services/userService';
import apiClient from '../services/api';
import './RanksPage.css';

const RanksPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth0();
  const [ranks, setRanks] = useState<BiblicalRank[]>([]);
  const [currentUserPoints, setCurrentUserPoints] = useState(0);
  const [currentUserLevel, setCurrentUserLevel] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedRank, setSelectedRank] = useState<BiblicalRank | null>(null);
  const [rankDistribution, setRankDistribution] = useState<{[key: number]: {count: number, percentage: number}}>({});

  useEffect(() => {
    const fetchRanks = async () => {
      try {
        setLoading(true);
        
        // Get all ranks
        const allRanks = await ranksService.getBiblicalRanks();
        setRanks(allRanks);
        
        // Get current user info if authenticated
        if (isAuthenticated && user) {
          const currentUser = authService.getCurrentUser();
          if (currentUser) {
            const userProfile = await userService.getUserProfile(currentUser.auth0Id);
            setCurrentUserPoints(userProfile.totalPoints);
            setCurrentUserLevel(userProfile.currentLevel || 1);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch ranks:', error);
        setLoading(false);
      }
    };
    
    fetchRanks();
  }, [isAuthenticated, user]);

  // Fetch leaderboard data and calculate rank distribution
  useEffect(() => {
    const fetchRankDistribution = async () => {
      try {
        const response = await apiClient.get('/leaderboard');
        const leaderboardData = response.data.data.leaderboard || [];

        // Count users at each rank level
        const rankCounts: {[key: number]: number} = {};
        leaderboardData.forEach((user: any) => {
          const level = user.currentLevel || 1;
          rankCounts[level] = (rankCounts[level] || 0) + 1;
        });

        // Calculate percentages
        const totalUsers = leaderboardData.length;
        const distribution: {[key: number]: {count: number, percentage: number}} = {};

        for (let level = 1; level <= 15; level++) {
          const count = rankCounts[level] || 0;
          const percentage = totalUsers > 0 ? Math.round((count / totalUsers) * 100) : 0;
          distribution[level] = { count, percentage };
        }

        setRankDistribution(distribution);
      } catch (error) {
        console.error('Failed to fetch rank distribution:', error);
        // Set default empty distribution
        const distribution: {[key: number]: {count: number, percentage: number}} = {};
        for (let level = 1; level <= 15; level++) {
          distribution[level] = { count: 0, percentage: 0 };
        }
        setRankDistribution(distribution);
      }
    };

    fetchRankDistribution();
  }, []);


  const RankGridItem: React.FC<{
    rank: BiblicalRank;
    currentUserPoints: number;
    currentUserLevel: number;
    onSelect: (rank: BiblicalRank) => void;
    rankDistribution: {[key: number]: {count: number, percentage: number}};
  }> = ({ rank, currentUserPoints, currentUserLevel, onSelect, rankDistribution }) => {
    if (!rank) return <div className="rank-grid-item empty"></div>;

    const isCurrentRank = rank.level === currentUserLevel;
    const isUnlocked = currentUserPoints >= rank.pointsRequired;
    const tierColor = getTierColor(rank.tier);
    const imagePath = getRankImagePath(rank.name);
    const distribution = rankDistribution[rank.level] || { count: 0, percentage: 0 };

    return (
      <div 
        className={`rank-grid-item ${isCurrentRank ? 'current' : ''} ${isUnlocked ? 'unlocked' : 'locked'}`}
        onClick={() => onSelect(rank)}
        title={`${rank.name} - ${rank.pointsRequired.toLocaleString()} points`}
      >
        <div className="rank-icon-wrapper">
          <img 
            src={imagePath} 
            alt={rank.name} 
            className="rank-icon"
            style={{ 
              opacity: isUnlocked ? 1 : 0.6,
              filter: isUnlocked ? 'none' : 'grayscale(30%) brightness(0.8)'
            }}
          />
          {isCurrentRank && (
            <div className="current-indicator" style={{ borderColor: tierColor }}>
              <div className="current-pulse" style={{ backgroundColor: tierColor }}></div>
            </div>
          )}
        </div>
        <div className="rank-name">{rank.name}</div>
        <div className="rank-percentage">
          {distribution.percentage}% of players
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="ranks-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Biblical Ranks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ranks-page">
      {/* Decorative elements */}
      <div className="leaderboard-decoration leaderboard-decoration-1"></div>
      <div className="leaderboard-decoration leaderboard-decoration-2"></div>
      <div className="leaderboard-decoration leaderboard-decoration-3"></div>
      
      {/* Additional decorative stars */}
      <div className="star star-1" style={{ top: '15%', right: '15%', width: '30px', height: '30px', opacity: '0.2', animation: 'bounce 3s infinite ease-in-out' }}></div>
      <div className="star star-2" style={{ top: '35%', left: '10%', width: '20px', height: '20px', opacity: '0.15', animation: 'bounce 4s infinite ease-in-out' }}></div>
      <div className="star star-3" style={{ bottom: '25%', right: '8%', width: '25px', height: '25px', opacity: '0.2', animation: 'bounce 5s infinite ease-in-out' }}></div>
      
      <div className="ranks-content">
        <header className="leaderboard-header">
          <h1 className="leaderboard-title">
            <Trophy className="leaderboard-crown" size={48} />
            Biblical Knowledge Journey
          </h1>
          <p className="leaderboard-subtitle">15 Ranks from Seeker to Biblical Master</p>
        </header>
        {/* Main 3x3 Grid */}
        <div className="main-ranks-grid">
          <div className="grid-header">
            <div className="tier-label">FOUNDATION</div>
            <div className="tier-label">GROWTH</div>
            <div className="tier-label">WISDOM</div>
          </div>
          
          <div className="ranks-grid-3x3">
            {/* Row 1 */}
            <div className="rank-row">
              <RankGridItem rank={ranks[0]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
              <RankGridItem rank={ranks[3]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
              <RankGridItem rank={ranks[6]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
            </div>
            
            {/* Row 2 */}
            <div className="rank-row">
              <RankGridItem rank={ranks[1]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
              <RankGridItem rank={ranks[4]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
              <RankGridItem rank={ranks[7]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
            </div>
            
            {/* Row 3 */}
            <div className="rank-row">
              <RankGridItem rank={ranks[2]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
              <RankGridItem rank={ranks[5]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
              <RankGridItem rank={ranks[8]} currentUserPoints={currentUserPoints} currentUserLevel={currentUserLevel} onSelect={setSelectedRank} rankDistribution={rankDistribution} />
            </div>
          </div>
        </div>
        
        {/* Premium Tiers Grid */}
        <div className="premium-ranks-grid">
          <div className="premium-tier">
            <div className="premium-tier-header">
              <h3 className="premium-tier-title" style={{ color: getTierColor('mastery') }}>MASTERY</h3>
            </div>
            <div className="premium-tier-ranks">
              {ranks.slice(9, 12).map((rank) => (
                <RankGridItem
                  key={rank.level}
                  rank={rank}
                  currentUserPoints={currentUserPoints}
                  currentUserLevel={currentUserLevel}
                  onSelect={setSelectedRank}
                  rankDistribution={rankDistribution}
                />
              ))}
            </div>
          </div>
          
          <div className="premium-tier">
            <div className="premium-tier-header">
              <h3 className="premium-tier-title" style={{ color: getTierColor('transcendence') }}>TRANSCENDENCE</h3>
            </div>
            <div className="premium-tier-ranks">
              {ranks.slice(12).map((rank) => (
                <RankGridItem
                  key={rank.level}
                  rank={rank}
                  currentUserPoints={currentUserPoints}
                  currentUserLevel={currentUserLevel}
                  onSelect={setSelectedRank}
                  rankDistribution={rankDistribution}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Rank Detail Modal */}
      {selectedRank && (
        <RankDetailModal 
          rank={selectedRank} 
          onClose={() => setSelectedRank(null)}
          isUnlocked={currentUserPoints >= selectedRank.pointsRequired}
          isCurrentRank={selectedRank.level === currentUserLevel}
        />
      )}
    </div>
  );
};

const RankDetailModal: React.FC<{
  rank: BiblicalRank;
  onClose: () => void;
  isUnlocked: boolean;
  isCurrentRank: boolean;
}> = ({ rank, onClose, isUnlocked, isCurrentRank }) => {
  const lore = getRankLore(rank.name);
  const tierColor = getTierColor(rank.tier);
  const imagePath = getRankImagePath(rank.name);
  
  return (
    <div className="rank-modal-overlay" onClick={onClose}>
      <div className="rank-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rank-modal-header">
          <div className="rank-modal-image">
            <img src={imagePath} alt={rank.name} style={{ opacity: isUnlocked ? 1 : 0.4 }} />
          </div>
          <div className="rank-modal-info">
            <div className="rank-modal-level" style={{ color: tierColor }}>
              Level {rank.level}
            </div>
            <h2 className="rank-modal-name">{rank.name}</h2>
            <div className="rank-modal-tier" style={{ color: tierColor }}>
              {rank.tier.toUpperCase()} TIER
            </div>
            <div className="rank-modal-points">
              {rank.pointsRequired.toLocaleString()} points required
            </div>
            {isCurrentRank && (
              <div className="rank-modal-current" style={{ backgroundColor: tierColor }}>
                YOUR CURRENT RANK
              </div>
            )}
          </div>
          <button className="rank-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="rank-modal-body">
          <blockquote className="rank-modal-quote" style={{ borderLeftColor: tierColor }}>
            {lore.openingQuote}
          </blockquote>
          
          <div className="rank-modal-description">
            {lore.mainDescription.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
          
          <div className="rank-modal-example">
            <h3 style={{ color: tierColor }}>Biblical Example:</h3>
            <h4>{lore.biblicalExampleTitle}</h4>
            <p className="example-description">{lore.biblicalExampleDescription}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RanksPage;